/* eslint-disable no-template-curly-in-string */
import React, { useState, useCallback, useEffect } from "react"

import { navigate, useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"

import * as styles from "./login.module.scss"
import {
  handleLogin,
  handleCreateAccount,
  handleUpdateAccount,
  handleResetPassword,
  handleConfirmPassword,
  isLoggedIn,
} from "../../../helpers/auth"
import UrlFunctions from "../../../helpers/url"
import CopyContainer from "../../atoms/copy-container"
import FormField from "../../atoms/form-field"
import Heading from "../../atoms/heading"
import Form from "../../molecules/form"

const Login = ({
  initialMode,
  currentEmail,
  currentFirstName,
  currentLastName,
  currentCountry,
  currentPhone,
  currentZipcode,
  light,
  standalone,
  cmId,
  customFields,
}) => {
  const { site, sanityLogin, allSanityDictionary, allSanityCountry } =
    useStaticQuery(graphql`
      {
        site {
          siteMetadata {
            siteUrl
          }
        }
        sanityLogin {
          login_message
          create_message
          _rawResetPassword
          _rawConfirmPassword
          forgot_password
          _rawConsent
        }
        allSanityCountry {
          nodes {
            abbreviation
            name
          }
        }
        allSanityDictionary {
          nodes {
            key {
              current
            }
            value
          }
        }
      }
    `)
  const [mode, setMode] = useState(initialMode)
  const [email, setEmail] = useState(currentEmail)
  const [password, setPassword] = useState("")
  const [repeatPassword, setRepeatPassword] = useState("")
  const [firstName, setFirstName] = useState(currentFirstName)
  const [lastName, setLastName] = useState(currentLastName)
  const [phone, setPhone] = useState(currentPhone)
  const [zipcode, setZipcode] = useState(currentZipcode)
  const [country, setCountry] = useState(
    currentCountry || process.env.GATSBY_COUNTRY
  )
  const [consent, setConsent] = useState("")
  const [formState, setFormState] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState(
    allSanityDictionary.nodes.find(
      (dictionaryItem) => dictionaryItem.key.current === "error_message"
    )?.value
  )

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    if (mode === "create" && password !== repeatPassword) {
      setFormState("error")
    } else {
      setFormState("pending")
      const submitFn = async () => {
        if (mode === "create") {
          return handleCreateAccount(
            email,
            password,
            repeatPassword,
            firstName,
            lastName,
            cmId,
            customFields
          )
        }
        if (mode === "edit") {
          return handleUpdateAccount(
            email,
            password,
            repeatPassword,
            firstName,
            lastName,
            country,
            phone,
            zipcode
          )
        }
        if (mode === "reset-password") {
          return handleResetPassword(email, site.siteMetadata.siteUrl)
        }
        if (mode.includes("confirm-password")) {
          const uid = initialMode.split("/")[1]
          const token = initialMode.split("/")[2]
          return handleConfirmPassword(password, repeatPassword, uid, token)
        }

        return handleLogin(email, password)
      }
      const response = await submitFn()
      if (response === true) {
        setFormState("success")
        if (mode === "reset-password") {
          setSuccessMessage(sanityLogin._rawResetPassword)
        } else if (mode.includes("confirm-password")) {
          setSuccessMessage(sanityLogin._rawConfirmPassword)
        } else if (mode === "edit") {
          if (password && repeatPassword)
            localStorage.removeItem("isAuthenticated")
          window.location.reload()
        } else if (standalone) {
          navigate("/account/profile")
        } else {
          window.location.reload()
        }
      } else {
        if (response) setErrorMessage(response)
        setFormState("error")
      }
    }
  })

  const switchMode = () => {
    const newMode = mode === "login" ? "create" : "login"
    setMode(newMode)
    if (standalone) navigate(`/account/${newMode}`)
  }

  const resetMode = () => {
    setMode("reset-password")
    if (standalone) navigate(`/account/reset-password`)
  }

  useEffect(() => {
    if (standalone && isLoggedIn()) {
      navigate("/account/profile")
    }
  }, [])

  useEffect(() => {
    setMode(initialMode)
    setSuccessMessage("")
    const unauthenticatedModes = ["create", "login"]
    if (
      unauthenticatedModes.includes(UrlFunctions.getParams().loginmode) &&
      unauthenticatedModes.includes(mode)
    ) {
      setMode(UrlFunctions.getParams().loginmode)
    }
  }, [initialMode])

  const action = () => {
    let key = "login"
    if (mode === "reset-password" || mode.includes("confirm-password"))
      key = "reset_password"
    if (mode === "edit") key = "account_submit"
    else if (mode === "create") key = "create_account"

    return allSanityDictionary.nodes.find((item) => item.key.current === key)
      ?.value
  }

  return (
    <div className={styles.login}>
      {mode !== "edit" && standalone ? <Heading>{action()}</Heading> : ""}
      <Form
        handleSubmit={handleSubmit}
        submitText={action()}
        formState={formState}
        errorMessage={errorMessage}
      >
        {!mode.includes("confirm-password") ? (
          <FormField
            label={
              allSanityDictionary.nodes.find(
                (dictionaryItem) => dictionaryItem.key.current === "email"
              )?.value
            }
            form="login"
            field="email"
            type="email"
            value={email}
            changeEvent={(e) => {
              setEmail(e.target.value)
            }}
            required
            light={light}
          />
        ) : (
          ""
        )}
        {mode !== "reset-password" ? (
          <FormField
            label={
              allSanityDictionary.nodes.find(
                (dictionaryItem) => dictionaryItem.key.current === "password"
              )?.value
            }
            form="login"
            field="password"
            type="password"
            value={password}
            changeEvent={(e) => {
              setPassword(e.target.value)
            }}
            required={mode !== "edit" || repeatPassword}
            light={light}
          />
        ) : (
          ""
        )}
        {mode !== "login" && mode !== "reset-password" ? (
          <FormField
            label={
              allSanityDictionary.nodes.find(
                (dictionaryItem) =>
                  dictionaryItem.key.current === "repeat_password"
              )?.value
            }
            form="login"
            field="repeatPassword"
            type="password"
            value={repeatPassword}
            changeEvent={(e) => {
              setRepeatPassword(e.target.value)
            }}
            required={mode !== "edit" || password}
            light={light}
          />
        ) : (
          ""
        )}
        {mode === "create" || mode === "edit" ? (
          <>
            <FormField
              label={
                allSanityDictionary.nodes.find(
                  (dictionaryItem) =>
                    dictionaryItem.key.current === "first_name"
                )?.value
              }
              form="login"
              field="firstName"
              type="text"
              value={firstName}
              changeEvent={(e) => {
                setFirstName(e.target.value)
              }}
              required
              light={light}
            />
            <FormField
              label={
                allSanityDictionary.nodes.find(
                  (dictionaryItem) => dictionaryItem.key.current === "last_name"
                )?.value
              }
              form="login"
              field="lastName"
              type="text"
              value={lastName}
              changeEvent={(e) => {
                setLastName(e.target.value)
              }}
              required
              light={light}
            />
            {mode === "edit" ? (
              <>
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (dictionaryItem) =>
                        dictionaryItem.key.current === "country"
                    )?.value
                  }
                  form="login"
                  field="country"
                  value={country}
                  type="select"
                  changeEvent={(e) => {
                    setCountry(e.target.value)
                  }}
                  options={allSanityCountry.nodes.map((c) => ({
                    name: c.name,
                    value: c.abbreviation,
                  }))}
                  required
                  light={light}
                />
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (dictionaryItem) => dictionaryItem.key.current === "phone"
                    )?.value
                  }
                  form="login"
                  field="phone"
                  type="tel"
                  value={phone}
                  changeEvent={(e) => {
                    setPhone(e.target.value)
                  }}
                  required
                  light={light}
                />
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (dictionaryItem) =>
                        dictionaryItem.key.current === "zipcode"
                    )?.value
                  }
                  form="login"
                  field="zipcode"
                  type="text"
                  value={zipcode}
                  changeEvent={(e) => {
                    setZipcode(e.target.value)
                  }}
                  required
                  light={light}
                />
              </>
            ) : (
              ""
            )}
            {mode === "create" ? (
              <FormField
                labelHtml={sanityLogin._rawConsent}
                form="contest"
                field="consent"
                value="consent"
                type="checkbox"
                changeEvent={() => {
                  setConsent(!consent)
                }}
                checked={consent}
                required
                light={light}
              />
            ) : (
              ""
            )}
          </>
        ) : (
          ""
        )}
      </Form>
      {mode === "login" || mode === "create" ? (
        <CopyContainer>
          <div>
            {mode === "create" ? (
              <span
                role="button"
                tabIndex="0"
                onClick={switchMode}
                onKeyPress={switchMode}
                className={`${styles.link} ${light ? styles.link__light : ""}`}
              >
                {sanityLogin.login_message}
              </span>
            ) : (
              <CopyContainer>
                <span
                  role="button"
                  tabIndex="0"
                  onClick={switchMode}
                  onKeyPress={switchMode}
                  className={`${styles.link} ${light ? styles.link__light : ""}`}
                >
                  {sanityLogin.create_message}
                </span>
                <br />
                <span
                  role="button"
                  tabIndex="0"
                  onClick={resetMode}
                  onKeyPress={resetMode}
                  className={`${styles.link} ${light ? styles.link__light : ""}`}
                >
                  {sanityLogin.forgot_password}
                </span>
              </CopyContainer>
            )}
          </div>
        </CopyContainer>
      ) : (
        ""
      )}
      {successMessage ? <CopyContainer content={successMessage} /> : ""}
    </div>
  )
}

export default Login

Login.propTypes = {
  initialMode: PropTypes.string,
  currentEmail: PropTypes.string,
  currentFirstName: PropTypes.string,
  currentLastName: PropTypes.string,
  currentCountry: PropTypes.string,
  currentPhone: PropTypes.string,
  currentZipcode: PropTypes.string,
  light: PropTypes.bool,
  standalone: PropTypes.bool,
  cmId: PropTypes.string,
  customFields: PropTypes.array,
}

Login.defaultProps = {
  initialMode: "login",
  currentCountry: process.env.GATSBY_COUNTRY,
  currentEmail: "",
  currentFirstName: "",
  currentLastName: "",
  currentPhone: "",
  currentZipcode: "",
  light: false,
  standalone: true,
  cmId: "",
  customFields: [],
}
